import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { Animacao } from '../../core/tools/animacao';
import { ProdutoService } from '../../core/services/produtos/produto.service';
import { Produto } from '../../core/models/produtos/produto';
import { ProdutoLocal } from '../../core/models/produtos/produto-local';

export interface FiltroAtivo {
  tipo: string;
  texto: string;
  valor: string;
}

export interface ProdutoSelecionado {
  id: number;
  codigo: string;
  name: string;
  estoque: number;
  custoEditavel: number | null;
  precoEditavel: number | null;
}

@Component({
    selector: 'app-produtos-analise-margem',
    templateUrl: './produtos-analise-margem.component.html',
    styleUrls: ['./produtos-analise-margem.component.css'],
    preserveWhitespaces: false,
    animations: [Animacao],
    standalone: false
})
export class ProdutosAnaliseMargemComponent implements OnInit, OnDestroy {
  carregando = false;
  carregandoFiltros = false;
  buscaRealizada = false;

  formFiltro: UntypedFormGroup;

  readonly filtroProdutos = [
    { texto: 'Código',     valor: 'codigo' },
    { texto: 'Descrição',  valor: 'descricao' },
    { texto: 'Marca',      valor: 'marca' },
    { texto: 'Subgrupo 1', valor: 'subgrupo1' },
    { texto: 'Subgrupo 2', valor: 'subgrupo2' },
    { texto: 'Subgrupo 3', valor: 'subgrupo3' },
    { texto: 'Subgrupo 4', valor: 'subgrupo4' },
    { texto: 'Subgrupo 5', valor: 'subgrupo5' },
  ];

  marcasDisponiveis: string[] = [];
  subgrupos1Disponiveis: string[] = [];
  subgrupos2Disponiveis: string[] = [];
  subgrupos3Disponiveis: string[] = [];
  subgrupos4Disponiveis: string[] = [];
  subgrupos5Disponiveis: string[] = [];

  filtrosAtivos: FiltroAtivo[] = [];

  locaisOrdenados: string[] = [];
  estoqueMap: Map<number, Map<string, number>> = new Map();

  produtos: Produto[] = [];
  produtosSelecionados: ProdutoSelecionado[] = [];
  totalProdutos = 0;
  paginaAtual = 0;
  pageSize = 20;

  private busca$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private produtoService: ProdutoService
  ) {}

  get ffp() {
    return this.formFiltro.controls;
  }

  get filtroProdutosDisponiveis() {
    return this.filtroProdutos.filter(f => {
      if (f.valor === 'marca')      return this.marcasDisponiveis.length > 0;
      if (f.valor === 'subgrupo1')  return this.subgrupos1Disponiveis.length > 0;
      if (f.valor === 'subgrupo2')  return this.subgrupos2Disponiveis.length > 0;
      if (f.valor === 'subgrupo3')  return this.subgrupos3Disponiveis.length > 0;
      if (f.valor === 'subgrupo4')  return this.subgrupos4Disponiveis.length > 0;
      if (f.valor === 'subgrupo5')  return this.subgrupos5Disponiveis.length > 0;
      return true;
    });
  }

  ngOnInit(): void {
    this.formFiltro = this.fb.group({
      filtro: ['descricao'],
      valor: ['']
    });

    this.carregarFiltros();
    this.carregarLocais();

    this.busca$.pipe(
      switchMap(() => {
        this.carregando = true;
        return this.produtoService.searchPaged(
          this.filtrosAtivos.map(f => ({ tipo: f.tipo, valor: f.valor })),
          this.paginaAtual,
          this.pageSize
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(result => {
      this.carregando = false;
      this.buscaRealizada = true;
      this.produtos = result.items;
      this.totalProdutos = result.total;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private carregarLocais(): void {
    this.produtoService.getProdutoLocalByGuidClienteNapis().pipe(
      takeUntil(this.destroy$)
    ).subscribe(locais => {
      this.locaisOrdenados = [...new Set(locais.map(l => l.local))].sort();
      locais.forEach(l => {
        if (!this.estoqueMap.has(l.idproduto)) this.estoqueMap.set(l.idproduto, new Map());
        this.estoqueMap.get(l.idproduto).set(l.local, Number(l.estoque));
      });
    });
  }

  getEstoqueLocal(idproduto: number, local: string): number {
    const mapa = this.estoqueMap.get(idproduto);
    if (!mapa) return 0;
    const v = mapa.get(local);
    return v !== undefined ? v : 0;
  }

  private carregarFiltros(): void {
    this.carregandoFiltros = true;
    this.produtoService.getFiltroOpcoes().pipe(
      takeUntil(this.destroy$)
    ).subscribe(opcoes => {
      this.marcasDisponiveis = opcoes.marcas;
      this.subgrupos1Disponiveis = opcoes.subgrupos1;
      this.subgrupos2Disponiveis = opcoes.subgrupos2;
      this.subgrupos3Disponiveis = opcoes.subgrupos3;
      this.subgrupos4Disponiveis = opcoes.subgrupos4;
      this.subgrupos5Disponiveis = opcoes.subgrupos5;
      this.carregandoFiltros = false;
    });
  }

  alterarFiltro(tipo: string): void {
    this.formFiltro.get('valor').setValue('');
  }

  adicionarFiltro(): void {
    const tipo: string = this.formFiltro.get('filtro').value;
    const valor: string = (this.formFiltro.get('valor').value || '').trim();
    if (!valor) return;
    if (this.filtrosAtivos.some(f => f.tipo === tipo && f.valor.toLowerCase() === valor.toLowerCase())) return;
    const found = this.filtroProdutos.find(f => f.valor === tipo);
    const texto = found ? found.texto : tipo;
    this.filtrosAtivos.push({ tipo, texto, valor });
    this.formFiltro.get('valor').setValue('');
  }

  removerFiltro(index: number): void {
    this.filtrosAtivos.splice(index, 1);
  }

  buscar(): void {
    if (this.filtrosAtivos.length === 0) return;
    this.paginaAtual = 0;
    this.busca$.next();
  }

  limpar(): void {
    this.filtrosAtivos = [];
    this.produtos = [];
    this.totalProdutos = 0;
    this.paginaAtual = 0;
    this.buscaRealizada = false;
    this.formFiltro.reset({ filtro: 'descricao', valor: '' });
  }

  get totalPaginas(): number {
    return Math.ceil(this.totalProdutos / this.pageSize);
  }

  mudarPagina(pagina: number): void {
    this.paginaAtual = pagina - 1;
    this.busca$.next();
  }

  mudarPageSize(size: number): void {
    this.pageSize = size;
    this.paginaAtual = 0;
    this.busca$.next();
  }

  estaSelecionado(id: number): boolean {
    return this.produtosSelecionados.some(p => p.id === id);
  }

  toggleSelecionado(produto: Produto): void {
    const index = this.produtosSelecionados.findIndex(p => p.id === produto.id);
    if (index >= 0) {
      this.produtosSelecionados.splice(index, 1);
    } else {
      this.produtosSelecionados.push({
        id: produto.id,
        codigo: produto.codigo,
        name: produto.name,
        estoque: produto.estoque,
        custoEditavel: produto.custo > 0 ? produto.custo : null,
        precoEditavel: produto.price > 0 ? produto.price : null
      });
    }
  }

  removerSelecionado(index: number): void {
    this.produtosSelecionados.splice(index, 1);
  }

  calcularMargemSelecionado(ps: ProdutoSelecionado): number | null {
    if (!ps.custoEditavel || ps.custoEditavel <= 0 || !ps.precoEditavel || ps.precoEditavel <= 0) return null;
    return (1 - ps.custoEditavel / ps.precoEditavel) * 100;
  }

  get margemMedia(): number | null {
    const validos = this.produtosSelecionados.filter(
      p => p.custoEditavel != null && p.custoEditavel > 0 && p.precoEditavel != null && p.precoEditavel > 0
    );
    if (validos.length === 0) return null;
    const soma = validos.reduce((acc, p) => acc + (1 - (p.custoEditavel as number) / (p.precoEditavel as number)) * 100, 0);
    return soma / validos.length;
  }

  trackById(_index: number, item: Produto): number {
    return item.id;
  }
}
